import React, { useState, useEffect, useRef } from "react";
import { MessagesSquare, Send, Loader2, CornerDownLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Markdown } from "@/components/common/Markdown";
import { DetailLevel } from "@/components/common/DetailLevel";
import { aiApi, flashcardsApi, getAiDetail, setAiDetail } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";
import { ChatQuickActions } from "@/components/reader/ChatQuickActions";

export const ChatPanel = ({ docId, onJumpToWord, seed }) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [detail, setDetail] = useState(getAiDetail());
    const scrollRef = useRef(null);
    const seedTsRef = useRef(null);
    const abortRef = useRef(null);

    const suggestions = t("chat.suggestions");

    useEffect(() => {
        (async () => {
            try {
                const hist = await aiApi.chatHistory(docId);
                setMessages(hist || []);
            } catch (e) { /* ignore */ }
        })();
        return () => abortRef.current?.abort?.();
    }, [docId]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, sending]);

    const changeDetail = (d) => { setDetail(d); setAiDetail(d); };

    const send = (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        setInput("");
        // user bubble + streaming assistant placeholder
        setMessages((m) => [...m, { role: "user", content: msg, _local: true }, { role: "assistant", content: "", sources: [], _streaming: true }]);
        setSending(true);

        const patchLast = (patch) => setMessages((m) => {
            const copy = [...m];
            for (let i = copy.length - 1; i >= 0; i--) {
                if (copy[i].role === "assistant") { copy[i] = typeof patch === "function" ? patch(copy[i]) : { ...copy[i], ...patch }; break; }
            }
            return copy;
        });

        abortRef.current = aiApi.chatStream(docId, msg, {
            language: "auto",
            detail,
            onDelta: (tok) => patchLast((a) => ({ ...a, content: a.content + tok })),
            onDone: ({ sources, message_id }) => {
                patchLast((a) => ({ ...a, sources: sources || [], _streaming: false, id: message_id }));
                setSending(false);
            },
            onError: () => {
                patchLast((a) => ({ ...a, content: a.content || t("chat.errorMsg"), _streaming: false }));
                setSending(false);
                toast.error(t("chat.error"));
            },
        });
    };

    const clear = async () => {
        try { await aiApi.clearChat(docId); setMessages([]); } catch (e) {}
    };

    // Click-to-Explain "Ask AI" handoff: auto-send the seeded question once.
    useEffect(() => {
        if (seed?.ts && seed.ts !== seedTsRef.current && seed.text && !sending) {
            seedTsRef.current = seed.ts;
            send(seed.text);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seed?.ts]);

    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div className="flex items-center gap-2 text-t1">
                    <MessagesSquare className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <span className="font-sans-display text-sm font-semibold">{t("chat.title")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <DetailLevel value={detail} onChange={changeDetail} compact />
                    {messages.length > 0 && (
                        <Button data-testid="chat-clear-button" variant="ghost" size="icon" className="h-8 w-8 text-t4 hover:text-t1" onClick={clear}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="chat-messages">
                {messages.length === 0 && !sending && (
                    <div className="flex h-full flex-col items-center justify-center py-8 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]">
                            <MessagesSquare className="h-6 w-6" />
                        </div>
                        <p className="mt-4 max-w-xs text-sm text-t3">{t("chat.empty")}</p>
                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                            {(Array.isArray(suggestions) ? suggestions : []).map((s) => (
                                <button key={s} data-testid="chat-suggestion" onClick={() => send(s)} className="rounded-full border border-border bg-surface-2 px-3 py-1.5 text-xs text-t3 transition-colors hover:bg-surface-3 hover:text-t1">{s}</button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((m, i) => {
                    const isLastAssistant = m.role === "assistant" && !m._streaming && !!m.content && i === messages.length - 1;
                    return (
                    <div key={i}>
                        <div className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                        <div className={cn(
                            "max-w-[88%] rounded-2xl px-4 py-2.5 text-sm",
                            m.role === "user"
                                ? "bg-surface-3 text-t1"
                                : "border border-border bg-surface-2 text-t2"
                        )}>
                            {m.role === "assistant" ? (
                                (m._streaming && !m.content) ? (
                                    <div className="flex items-center gap-2 text-t3">
                                        <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--primary))]" />
                                        <span>{t("chat.sending")}</span>
                                    </div>
                                ) : (
                                    <>
                                        <Markdown className="text-[14px] leading-relaxed">{m.content}</Markdown>
                                        {m._streaming && <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-[hsl(var(--primary))] align-middle" />}
                                        {!m._streaming && m.sources?.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-1.5">
                                                {m.sources.map((s) => (
                                                    <button
                                                        key={s.n}
                                                        data-testid="chat-citation-chip"
                                                        title={s.excerpt}
                                                        onClick={() => onJumpToWord?.(s.word_start)}
                                                        className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-3/60 px-2 py-0.5 font-mono-ui text-[11px] text-t2 transition-colors hover:bg-surface-3 hover:text-t1"
                                                    >
                                                        [{s.n}] {s.section_title ? s.section_title.slice(0, 18) : "source"}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )
                            ) : (
                                <span>{m.content}</span>
                            )}
                        </div>
                        </div>
                        {isLastAssistant && (
                            <div className="mt-1.5 flex justify-start">
                                <div className="w-full max-w-[88%]">
                                    <ChatQuickActions
                                        key={`qa-${i}`}
                                        question={messages[i - 1]?.role === "user" ? messages[i - 1].content : ""}
                                        answer={m.content}
                                        onFollowUp={(text) => send(text)}
                                        makeCardFn={({ question, answer }) => flashcardsApi.fromChat(docId, { question, answer })}
                                        saveCardFn={(card) => flashcardsApi.add(docId, card)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>

            <div className="border-t border-border p-3">
                <div className="relative">
                    <Textarea
                        data-testid="chat-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={t("chat.placeholder")}
                        className="min-h-[52px] resize-none bg-surface-2 pr-12 text-sm"
                    />
                    <Button data-testid="chat-send-button" size="icon" className="absolute bottom-2 right-2 h-8 w-8" disabled={sending || !input.trim()} onClick={() => send()}>
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                </div>
                <div className="mt-1.5 flex items-center gap-1 px-1 font-mono-ui text-[10px] text-t4">
                    <CornerDownLeft className="h-3 w-3" /> {t("chat.sendHint")} · {t("chat.newlineHint")}
                </div>
            </div>
        </div>
    );
};
